import DynamicFieldsHandler from "./dynamicFieldsHandler";
import ObjectModel from "../../objectModel";
import ObjectModelProperty from "../../objectModelProperties/objectModelProperty";
import ObjectModelPropertyOptions from "../options/objectModelPropertyOptions";
import {ObjectModelPropertyRootType} from "../../../enums/objectModelPropertyType";
import {EnumHelper} from "../../../helpers/enumHelper";
import ObjectModelPropertyDefinition from "../../objectModelProperties/objectModelPropertyDefinition";
import {IDynamicDataConfig, IDynamicObjectModelPropertyConfig} from "../../../interfaces/dynamicFieldsInterfaces";
import {DynamicReferenceType} from "../../../enums/dynamicReferenceType";
import ObjectModelBuiltInPropertyDecorator from "~/models/decorators/objectModelBuiltinPropertyDecorator";

type TModes = 'flat' | 'type';

interface IObjectModelPropertyFieldsHandlerConfig {
  mode: 'flat' | 'type',
  objectModel?: ObjectModel,
  pillColor?: string,
  elementOptions?: ObjectModelPropertyOptions
  meta?: (selectedItem) => string
  excluded_property_client_ids?: Array<string>
}

export default class ObjectModelPropertyDynamicFieldsHandler extends DynamicFieldsHandler {
  SECTIONS = ['objectModelPropertyData'];
  dynamicMessage: string;
  showDynamicMessage: boolean;
  mode: TModes;
  objectModel: ObjectModel;
  excluded_property_client_ids?: Array<string> = []
  pillColor: string = 'light-grey';

  elementOptions = new ObjectModelPropertyOptions();


  /**
   *
   * @param options: { mode: 'flat' | 'type', objectModel?: ObjectModel }
   *
   * `'flat'` mode: displays flat list of all properties
   *
   *    > Object Model Properties
   *      - Property 1
   *      - Property 2
   *
   * `'type'` mode: displays list of properties nested by type
   *
   *    > Object Model Properties
   *      > Short Text
   *        - Property 1
   *      > Long Text
   *        - Property 2
   *
   */

  constructor(options: IObjectModelPropertyFieldsHandlerConfig = {mode: 'flat'}) {
    super(options);
    this.mode = options.mode;
    this.objectModel = options.objectModel;
    this.pillColor = options.pillColor;
    if(options.excluded_property_client_ids){
      this.excluded_property_client_ids = options.excluded_property_client_ids
    }
    if (options.elementOptions) {
      this.elementOptions = options.elementOptions
    }
  }

  objectModelPropertiesSection(q: string): Pick<IDynamicObjectModelPropertyConfig, 'properties' | 'select'> {
    const section: Pick<IDynamicObjectModelPropertyConfig, 'properties' | 'select'> = {
      properties: [],
      select: this.selectObjectModelProperty.bind(this),
    };
    section.properties.push(
      ...this.elementOptions.getElements(this.objectModel).filter(prop => {
        return prop.labelTitleDisplay.toLowerCase().match(q) !== null && !this.excluded_property_client_ids.includes(prop.client_id);
      })
    );
    return section;
  }

  objectModelPropertySections(q: string): IDynamicObjectModelPropertyConfig[] {
    const sections = EnumHelper.toArray(ObjectModelPropertyRootType)
      .map(t => {
        return this.buildObjectModelPropertySection(t, []);
      });
    this.elementOptions.matchElements(this.objectModel, q).forEach(property => {
      const section = sections.find(s => s.propertyType === property.property_type);
      if (section) {
        section.properties.push(property);
      }
    });
    return sections;
  }

  hasObjectModelPropertyMatch(q: string): boolean {
    return this.objectModelPropertiesSection(q).properties.length > 0;
  }

  hasObjectModelPropertySortedMatch(q: string): boolean {
    return this.objectModelPropertySections(q).some(section => section.properties.length > 0);
  }

  private selectObjectModelProperty(property: ObjectModelProperty | ObjectModelBuiltInPropertyDecorator): IDynamicDataConfig {
    const data = {
      type: DynamicReferenceType.ObjectModelProperty,
      rootId: this.objectModel.id as string,
      pillColor: this.pillColor,
      id: property.client_id,
      label: property.labelTitleDisplay,
    };

    // builtin OM properties do not have client_ids, so using the builtin_property_type
    if (property.builtinPropertyType) {
      data.id = property.builtinPropertyType.toString();
    }
    return this.select(property, data);
  }

  private buildObjectModelPropertySection(propertyType: ObjectModelPropertyRootType, properties: ObjectModelProperty[]): IDynamicObjectModelPropertyConfig {
    return {
      propertyType: propertyType,
      propertyTypeName: ObjectModelPropertyDefinition.getDisplayName(propertyType),
      properties: properties,
      select: this.selectObjectModelProperty.bind(this),
    };
  }
}